import * as React from 'react';
import { useIntl } from 'react-intl';
import { get } from 'lodash';
import { EEligibilityStatus, Insurance } from '@lib/resources/types';
import moment from 'moment';
import messages from '../InsuranceCapture.messages';

export default function useMakeGetInsuranceAnswerTextsToShow() {
  const { formatMessage, formatDate } = useIntl();

  const eligibilityStatusMessages = React.useMemo(
    () => ({
      [EEligibilityStatus.Active]: (date: string) => formatMessage(messages.eligible, { date }),
      [EEligibilityStatus.Inactive]: (date: string) => formatMessage(messages.notEligible, { date }),
      [EEligibilityStatus.Failed]: (date: string) => formatMessage(messages.eligibilityFailed, { date }),
      [EEligibilityStatus.Unknown]: (date: string) => formatMessage(messages.unknown, { date }),
    }),
    [formatMessage],
  );

  const getInsuranceVerificationAnswers = React.useCallback(
    (insuranceObject: Insurance, shouldShowEligibility?: boolean) => {
      const relationshipToSubscriber = get(insuranceObject, 'relationshipToSubscriber.label', '');
      const relationshipToSubscriberLabel = relationshipToSubscriber ? `(${relationshipToSubscriber})` : '';
      const dateOfBirthObject = get(insuranceObject, 'subscriber.dateOfBirth', null);
      const dateOfBirthString = dateOfBirthObject
        ? `${dateOfBirthObject.month}-${dateOfBirthObject.day}-${dateOfBirthObject.year}`
        : '';
      const formattedDateOfBirth = dateOfBirthString
        ? formatDate(dateOfBirthString, {
            year: 'numeric',
            day: '2-digit',
            month: '2-digit',
          })
        : '-';

      // Add details to be showed on the formAnswer
      const insuranceAnwerTexts: Array<[string, string] | [string]> = [
        [
          `${insuranceObject.payerName ?? ''} (${
            insuranceObject.primary
              ? formatMessage(messages.primaryInsurance)
              : formatMessage(messages.additionalInsurance)
          })`,
        ],
        [formatMessage(messages.memberName), `${insuranceObject.memberName ?? '-'} ${relationshipToSubscriberLabel}`],
        [formatMessage(messages.subscriberDateOfBirth), formattedDateOfBirth],
        [formatMessage(messages.memberNumber), insuranceObject.memberNumber ?? '-'],
        [formatMessage(messages.insuranceGroupName), insuranceObject.insuranceGroupName ?? '-'],
        [
          formatMessage(messages.startDate),
          insuranceObject.startDate ? moment(insuranceObject.startDate).format('MM-DD-YYYY') : '-',
        ],
        [
          formatMessage(messages.endDate),
          insuranceObject.endDate ? moment(insuranceObject.endDate).format('MM-DD-YYYY') : '-',
        ],
      ];

      if (shouldShowEligibility && insuranceObject.verification) {
        insuranceAnwerTexts.push(
          [
            formatMessage(messages.planStatus),
            insuranceObject.status === 'active' ? formatMessage(messages.active) : formatMessage(messages.inactive),
          ],
          [
            formatMessage(messages.eligibilityResult),

            eligibilityStatusMessages[insuranceObject.verification.status || EEligibilityStatus.Unknown](
              formatDate(insuranceObject.verification.updatedAt, {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                dayPeriod: 'short',
              }),
            ),
          ],
        );
      }

      return insuranceAnwerTexts;
    },
    [eligibilityStatusMessages, formatDate, formatMessage],
  );
  return getInsuranceVerificationAnswers;
}
